import * as XLSX from 'xlsx'
import type { ActiveChip } from '@/hooks/use-table-filters'

/**
 * The banner an export carries when it is not the whole table.
 *
 * Every Analysis export writes the FILTERED rows, so a sheet produced under a
 * filter is indistinguishable from the complete register once it leaves the
 * app. Filters also persist per bid, which means the narrowing may have been
 * set weeks earlier and forgotten. A Criticality = Mandatory filter left on
 * turns a 53-row risk register into 18 rows numbered 1–18 — and that is what
 * gets emailed to a bid director.
 *
 * Most exports renumber the rows they write from 1, and the note says so. The
 * compliance matrix does not: its REQ numbers are stable per bid, so a filtered
 * sheet keeps them, gaps and all — it passes its own `numberingNote`.
 *
 * The note describes the FILE, not the UI, so it counts anything that kept rows
 * out: the filter chips, and `extras` for narrowing that has no chip (the
 * coverage strip and the collapsed post-award group on the compliance matrix).
 */
export interface ExportScope {
  /** The filter chips in force when Export was pressed. */
  chips: ActiveChip[]
  /** Rows written to the sheet. */
  shown: number
  /** Rows the table would hold with nothing narrowing it. */
  total: number
  /** Narrowing with no chip of its own, already phrased for a reader. */
  extras?: string[]
  /**
   * How the sheet's numbers relate to the full table, as one sentence. Defaults
   * to the renumbered case; a table with stable numbers says so instead.
   */
  numberingNote?: string
}

const RENUMBERED_NOTE = 'Numbering restarts at 1.'

export function exportScopeNote({
  chips,
  shown,
  total,
  extras = [],
  numberingNote = RENUMBERED_NOTE,
}: ExportScope): string | null {
  // Trigger on the COUNTS, not on the chips: narrowing that leaves no chip
  // behind is exactly the kind that makes a short sheet read as a whole one.
  if (shown >= total) return null

  const byGroup = new Map<string, string[]>()
  for (const c of chips) {
    const labels = byGroup.get(c.groupLabel)
    if (labels) labels.push(c.label)
    else byGroup.set(c.groupLabel, [c.label])
  }

  const causes = [
    ...[...byGroup].map(([group, labels]) => `${group}: ${labels.join(', ')}`),
    ...extras,
  ]

  const head = `FILTERED VIEW — ${shown} of ${total} rows. ${numberingNote}`
  return causes.length > 0 ? `${head} Applied: ${causes.join('; ')}.` : head
}

/**
 * Lay the rows out under an optional banner row.
 *
 * With no note the sheet is exactly what `json_to_sheet` produces, so an
 * unfiltered export keeps the shape every consumer already expects. With one,
 * the data drops to row 2 and the note spans the header block — never past the
 * sheet's own range, which a merge on an empty export would do.
 */
export function buildExportSheet(
  rows: Array<Record<string, string | number>>,
  note: string | null
): XLSX.WorkSheet {
  if (!note) return XLSX.utils.json_to_sheet(rows)

  const sheet = XLSX.utils.sheet_add_aoa({}, [[note]], { origin: 'A1' })
  XLSX.utils.sheet_add_json(sheet, rows, { origin: 'A2' })

  const columnCount = Object.keys(rows[0] ?? {}).length
  if (columnCount > 1) {
    sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } }]
  }
  return sheet
}
